import jsPDF from "jspdf";
import moment from "moment";
import React from "react";
import { Button } from "reactstrap";
import "jspdf-autotable";
import { useSelector } from "react-redux";

const PrintBill = ({ order, restaurantCurrency }) => {
  const { user } = useSelector((state) => state.auth);
  const { restaurantsDetail } = useSelector((state) => state.restaurant);

  const thermalPrint = async (item) => {
    const timestamp = item?.createdAt;
    const unixTimestamp = timestamp._seconds + timestamp._nanoseconds / 1e9;
    const formattedDateTime = moment
      .unix(unixTimestamp)
      .format("DD/MM/YYYY HH:mm:ss A");

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;

    doc.setFontSize(26).setFont(undefined, "bold");
    const titleText = `${
      user?.type === "kitchen-waiter" ? restaurantsDetail?.name : user?.name
    } Restaurant`;
    const wrappedTitle = doc.splitTextToSize(titleText, pageWidth - 40);
    wrappedTitle.forEach((line, index) => {
      doc.text(line, pageWidth / 2, 15 + index * 8, { align: "center" });
    });
    const titleHeight = 15 + wrappedTitle.length * 8;

    doc.setFontSize(15).setFont(undefined, "normal");
    const locationText =
      user?.type === "kitchen-waiter"
        ? restaurantsDetail?.location
        : user?.location;
    const wrappedLocation = doc.splitTextToSize(locationText, pageWidth - 40);
    let locationStartY = titleHeight + 2;
    wrappedLocation.forEach((line, index) => {
      doc.text(line, pageWidth / 2, locationStartY + index * 6, {
        align: "center",
      });
    });
    const afterLocationY = locationStartY + wrappedLocation.length * 6 + 10;

    // --- Bill Info
    doc.setFontSize(20).setFont(undefined, "bold");
    doc.text("Bill Detail", 10, afterLocationY);
    doc.setFontSize(14).setFont(undefined, "normal");
    let y = afterLocationY + 10;

    doc.text(`Customer : ${item.customerName || "N/A"}`, 10, y);
    if (order.orderType === "dinein") {
      y += 10;
      doc.text(`Table no : ${item.tableNumber || "N/A"}`, 10, y);
    }
    doc.text(`Date : ${formattedDateTime}`, 10, y + 10);

    let tableStartY = y + 20;

    if (order.id) {
      doc.text(`Order ID : ${order.id.toString()}`, 10, tableStartY);
      const orderTypeFormatted =
        order?.orderType?.charAt(0).toUpperCase() +
          order?.orderType?.slice(1) || "N/A";
      doc.text(`Order Type : ${orderTypeFormatted}`, 10, tableStartY + 10);
      tableStartY += 20;
    }

    const tableData = [];

    item.order?.forEach((elem) => {
      // Add main item row
      tableData.push([
        elem.name || "N/A",
        elem.size || "N/A",
        elem.quantity?.toString() || "0",
        `${restaurantCurrency} ${elem.price * elem.quantity || 0}`,
      ]);

      // Add addon rows if available
      if (elem.addons && Array.isArray(elem.addons) && elem.addons.length > 0) {
        elem.addons.forEach((addon) => {
          const addonTotal = (addon.price || 0) * (addon.quantity || 1);
          tableData.push([
            `  + ${addon.name || "N/A"}`, // Indented with + to show it's an addon
            "-", // No size for addons
            addon.quantity?.toString() || "1",
            `${restaurantCurrency} ${addonTotal.toFixed(2)}`,
          ]);
        });
      }
    });

    doc.autoTable({
      head: [["Name", "Size", "Quantity", "Price"]],
      body: tableData,
      startY: tableStartY,
      styles: { fontSize: 12 },
      headStyles: {
        fillColor: [0, 0, 0],
        textColor: [255, 255, 255],
        halign: "center",
      },
      bodyStyles: { halign: "center" },
      theme: "grid",
      margin: { left: 10, right: 10 },
      didParseCell: function (data) {
        // Style addon rows differently
        if (
          data.cell.text &&
          data.cell.text[0] &&
          data.cell.text[0].startsWith("  +")
        ) {
          data.cell.styles.textColor = [100, 100, 100]; // Gray color for addons
          data.cell.styles.fontSize = 10; // Smaller font for addons
          data.cell.styles.fontStyle = "italic"; // Italic style for addons
        }
      },
    });

    let finalY = doc.lastAutoTable.finalY || tableStartY + 10;

    doc.setFontSize(14).setFont(undefined, "bold");
    doc.text("Items Total", 90, finalY + 20);
    doc.text(
      `${restaurantCurrency} ${(item?.totalPriceItem ? item?.totalPriceItem : item?.totalAmount|| 0).toFixed(2)}`,
      150,
      finalY + 20
    );

    const tipValue = Number(item?.tip);
    const safeTip = isNaN(tipValue) ? 0 : tipValue;

    doc.text("Tip", 90, finalY + 30);
    doc.text(`${restaurantCurrency} ${safeTip.toFixed(2)}`, 150, finalY + 30);

    let currentY = finalY + 40;

    if (item.appliedCharges && item.appliedCharges.length > 0) {
      item.appliedCharges.forEach((charge) => {
        let chargeLabel = `${charge.name} (${
          charge.type === "fixed"
            ? `${restaurantCurrency} ${charge.fee}`
            : `${charge.fee}%`
        })`;
        const wrappedText = doc.splitTextToSize(chargeLabel, 55);
        wrappedText.forEach((line, index) => {
          const yPos = currentY + index * 5;
          doc.text(line, 90, yPos);
          if (index === 0) {
            doc.text(
              `+ ${restaurantCurrency} ${Number(
                charge.addedAmount || 0
              ).toFixed(2)}`,
              150,
              yPos
            );
          }
        });
        currentY += wrappedText.length * 5 + 5;
      });
    }

    doc.text("Net Total", 90, currentY);
    doc.text(
      `${restaurantCurrency} ${(item?.finalPrice
        ? item?.finalPrice
        : item?.totalAmount || 0
      ).toFixed(2)}`,
      150,
      currentY
    );
    doc.line(90, currentY - 5, 180, currentY - 5);

    doc.setFontSize(14);
    doc.text("Powered by NextEats", pageWidth / 2, 275, { align: "center" });

    if (window.innerWidth <= 600) {
      doc.save("order-receipt.pdf");
    } else {
      doc.autoPrint();
      window.open(doc.output("bloburl"), "_blank");
    }
  };

  return (
    <div>
      <Button
        size="sm"
        className="mr-3 w-100 orders-table__process"
        onClick={() => thermalPrint(order)}
      >
        Print Bill
      </Button>
    </div>
  );
};

export default PrintBill;
